import React, {useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import {observer} from 'mobx-react';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import {Theme} from '@mui/system';

const styles = {
    drawer: {
        '& ::-webkit-scrollbar': {
            WebkitAppearance: 'none',
            width: 16,
        },
        '& ::-webkit-scrollbar-thumb': {
            backgroundColor: '#D9D9D9',
            backgroundClip: 'content-box',
            border: '6px solid transparent',
        },
    },
    icon: {
        position: 'absolute',
        top: 0,
        right: 0,
        padding: 1,
        margin: 2,
        cursor: 'pointer',
    },
    drawerContent: {
        paddingBottom: {xs: 3, lg: 4},
        maxHeight: {lg: 522},
        height: '100%',
        backgroundColor: '#fff',
        margin: 'auto',
        position: 'relative',
    },
    title: {
        paddingTop: {lg: 4},
        fontFamily: 'FredokaMedium',
        fontSize: 18,
        color: '#2E5B56',
        mb: {xs: 3, lg: 2},
        lineHeight: '27px',
        backgroundColor: '#fff',
    },
    text: {
        fontFamily: 'FredokaLight',
        fontSize: 16,
        color: '#000',
        lineHeight: '27px',
        '& a': {
            fontFamily: 'FredokaRegular',
            fontWeight: 500,
            textDecoration: 'underline',
        },
    },
    drawerText: {
        paddingLeft: 2,
        paddingBottom: {xs: 3, lg: 4},
        maxHeight: {lg: 522},
        height: 'calc(100% - 40px)',
        backgroundColor: '#fff',
        position: 'relative',
        border: {lg: '1px solid #3F9E5F'},
        overflowY: 'auto',
    },
    drawerSeparateTab: {
        marginTop: 2,
        position: 'relative',
        textAlign: 'center',
        '& .MuiLink-root': {
            color: '#174943',
            fontFamily: 'FredokaMedium',
            fontSize: 14,
            lineHeight: '22.4px',
            fontWeight: 500,
            textDecoration: 'underline',
            '&:hover': {
                cursor: 'pointer',
            },
        },
    },
};

interface ContentProps {
    title: string;
    content: string;
    onClose: () => void;
    href: string;
    showOpenInSeparateTab?: boolean;
}

const Content: React.FC<ContentProps> = ({title, content, onClose, href, showOpenInSeparateTab}) => {
    const contentRef = useRef(null);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [title, content]);

    return (
        <>
            <IconButton title="Close" sx={styles.icon} onClick={onClose} data-testid="consentDrawerClose">
                <Box component="svg" width="12px" height="12px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M22.8002 1.19995L1.2002 22.8M1.2002 1.19995L22.8002 22.8"
                        stroke="#174943"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />
                </Box>
            </IconButton>
            <Box sx={styles.drawerContent}>
                <Box sx={styles.drawerText} ref={contentRef}>
                    <Box component="p" sx={styles.title} data-testid="consentDrawerTitle">
                        {title}
                    </Box>
                    <Box component="div" sx={styles.text} data-testid="consentDrawerContent">
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{content}</ReactMarkdown>
                    </Box>
                </Box>
                {showOpenInSeparateTab && (
                    <Box sx={styles.drawerSeparateTab}>
                        <Link
                            onClick={() => {
                                window.open(href, '_blank');
                            }}
                        >
                            View in a separate tab
                        </Link>
                    </Box>
                )}
            </Box>
        </>
    );
};

interface ResponsiveDrawerProps {
    activeDrawer: {
        isOpen: boolean;
        title: string;
        content: string;
        href: string;
    };
    onClose: () => void;
    showOpenInSeparateTab?: boolean;
    forceRefresh?: boolean;
}

const ResponsiveDrawer: React.FC<ResponsiveDrawerProps> = observer(
    ({activeDrawer: {title, content, isOpen, href}, onClose, showOpenInSeparateTab = false}) => {
        const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
        return (
            <>
                {!isMobile && (
                    <Drawer
                        onClose={onClose}
                        open={isOpen}
                        anchor="right"
                        data-testid="consentDrawer"
                        variant="temporary"
                        hideBackdrop
                        ModalProps={{
                            disableScrollLock: true,
                        }}
                        sx={{
                            ...styles.drawer,
                            display: {xs: 'none', lg: 'flex'},
                            maxWidth: 391,
                            right: 0,
                            left: 'unset',
                            width: '100%',
                            '& .MuiDrawer-paper': {
                                backgroundColor: '#EFFCF3',
                                maxWidth: 391,
                                width: '100%',
                                px: '26px',
                                py: '24px',
                            },
                        }}
                    >
                        <Content
                            title={title}
                            content={content}
                            onClose={onClose}
                            href={href}
                            showOpenInSeparateTab={showOpenInSeparateTab}
                        />
                    </Drawer>
                )}

                {isMobile && (
                    <Drawer
                        onClose={onClose}
                        open={isOpen}
                        anchor="bottom"
                        variant="temporary"
                        sx={{
                            ...styles.drawer,
                            display: {xs: 'flex', lg: 'none'},
                            '& .MuiDrawer-paper': {
                                backgroundColor: '#FFFFFF',
                                maxHeight: '90%',
                                height: '100%',
                                paddingTop: '50px',
                                borderRadius: '16px 16px 0px 0px',
                            },
                        }}
                    >
                        <Content
                            title={title}
                            content={content}
                            onClose={onClose}
                            href={href}
                            showOpenInSeparateTab={showOpenInSeparateTab}
                        />
                    </Drawer>
                )}
            </>
        );
    },
);

export default ResponsiveDrawer;
